import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiBookOpen,
  FiChevronRight,
  FiChevronDown,
  FiCheck,
  FiFlag,
  FiShield,
  FiStar,
  FiAward,
  FiHeart,
  FiTrendingUp,
  FiDollarSign,
  FiUsers,
  FiMapPin,
  FiInfo
} = FiIcons;

const RepublicanParty = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedFaqs, setExpandedFaqs] = useState([]);

  const toggleFaq = (id) => {
    if (expandedFaqs.includes(id)) {
      setExpandedFaqs(expandedFaqs.filter(faqId => faqId !== id));
    } else {
      setExpandedFaqs([...expandedFaqs, id]);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FiBookOpen },
    { id: 'history', name: 'History', icon: FiFlag },
    { id: 'values', name: 'Values', icon: FiShield },
    { id: 'facts', name: 'Fun Facts', icon: FiStar },
    { id: 'achievements', name: 'Achievements', icon: FiAward },
    { id: 'faq', name: 'FAQ', icon: FiInfo }
  ];

  // Republican core values
  const coreValues = [
    {
      icon: FiShield,
      title: 'Constitutional Rights',
      description: 'Defending the Constitution and Bill of Rights as written by our Founding Fathers.'
    },
    {
      icon: FiStar,
      title: 'Limited Government',
      description: 'Supporting smaller federal government with more power returned to states and localities.'
    },
    {
      icon: FiTrendingUp,
      title: 'Free Markets',
      description: 'Promoting economic freedom, deregulation, and entrepreneurship to drive prosperity.'
    },
    {
      icon: FiDollarSign,
      title: 'Fiscal Responsibility',
      description: 'Advocating for balanced budgets, reduced government spending, and lower taxes.'
    },
    {
      icon: FiHeart,
      title: 'Traditional Values',
      description: 'Upholding family values, religious liberty, and the sanctity of life.'
    },
    {
      icon: FiUsers,
      title: 'Strong National Defense',
      description: 'Maintaining military strength and supporting our troops and veterans.'
    },
    {
      icon: FiMapPin,
      title: 'Secure Borders',
      description: 'Enforcing immigration laws and protecting our national sovereignty.'
    }
  ];

  // Republican achievements through history
  const achievements = [
    {
      year: '1862',
      president: 'Abraham Lincoln',
      title: 'Emancipation Proclamation',
      description: 'Issued by President Lincoln, declaring "that all persons held as slaves" within the rebellious states "are, and henceforward shall be free."'
    },
    {
      year: '1865',
      president: 'Abraham Lincoln',
      title: '13th Amendment',
      description: 'Abolished slavery throughout the United States. Every Republican in Congress voted for the 13th Amendment, while nearly all Democrats opposed it.'
    },
    {
      year: '1956-1961',
      president: 'Dwight D. Eisenhower',
      title: 'Interstate Highway System',
      description: 'Created the largest public works project in American history, revolutionizing transportation and commerce.'
    },
    {
      year: '1964',
      president: '',
      title: 'Civil Rights Act',
      description: 'A higher percentage of Republicans than Democrats voted for the Civil Rights Act, which outlawed discrimination based on race, color, religion, sex, or national origin.'
    },
    {
      year: '1981-1989',
      president: 'Ronald Reagan',
      title: 'Economic Revitalization',
      description: 'Implemented tax cuts and deregulation that led to economic growth and the creation of millions of jobs.'
    },
    {
      year: '1981-1989',
      president: 'Ronald Reagan',
      title: 'End of the Cold War',
      description: "Reagan's strong stance against communism contributed to the fall of the Soviet Union and the end of the Cold War."
    },
    {
      year: '2001-2009',
      president: 'George W. Bush',
      title: 'No Child Left Behind Act',
      description: 'Reformed education by requiring states to assess basic skills to receive federal funding, aimed at improving educational outcomes.'
    },
    {
      year: '2017-2021',
      president: 'Donald Trump',
      title: 'Tax Cuts and Jobs Act',
      description: 'Implemented major tax reform that reduced tax rates for businesses and individuals, spurring economic growth.'
    },
    {
      year: '2017-2021',
      president: 'Donald Trump',
      title: 'Historic Low Unemployment',
      description: 'Achieved record-low unemployment rates across all demographic groups before the COVID-19 pandemic.'
    },
    {
      year: '2017-2021',
      president: 'Donald Trump',
      title: 'Abraham Accords',
      description: 'Brokered historic peace agreements between Israel and multiple Arab nations, advancing peace in the Middle East.'
    }
  ];

  // Republican fun facts
  const funFacts = [
    {
      title: 'GOP Stands For "Grand Old Party"',
      description: 'The Republican Party is often referred to as the GOP, which stands for "Grand Old Party." This nickname was first used in the 1870s.'
    },
    {
      title: 'The Elephant Symbol',
      description: 'The elephant became the symbol of the Republican Party due to political cartoonist Thomas Nast, who first used it in an 1874 Harper\'s Weekly cartoon.'
    },
    {
      title: 'First Republican President',
      description: 'Abraham Lincoln was the first Republican president, elected in 1860. He led the nation through the Civil War and issued the Emancipation Proclamation.'
    },
    {
      title: 'Birthday of the Republican Party',
      description: 'The Republican Party was founded on March 20, 1854, in Ripon, Wisconsin, by anti-slavery activists and modernizers.'
    },
    {
      title: 'Republican Women\'s Firsts',
      description: 'The first woman elected to Congress, Jeannette Rankin, was a Republican from Montana, elected in 1916, before women could even vote nationally.'
    },
    {
      title: 'Republican Color',
      description: 'The association of Republicans with the color red is relatively recent. Before the 2000 election, TV networks didn\'t consistently use red for Republicans and blue for Democrats.'
    },
    {
      title: 'Longest-Serving Republican',
      description: 'Strom Thurmond holds the record for the longest-serving Republican senator, representing South Carolina for 48 years.'
    },
    {
      title: 'Republican Dominance',
      description: 'From 1860 to 1932, Republicans dominated the White House, controlling the presidency for 56 of those 72 years.'
    }
  ];

  // Republican FAQs
  const faqs = [
    {
      id: 1,
      question: 'What does the Republican Party stand for?',
      answer: 'The Republican Party stands for constitutional conservatism, limited government, free market economics, fiscal responsibility, strong national defense, traditional values, and individual liberty. Republicans believe in empowering individuals rather than expanding government control.'
    },
    {
      id: 2,
      question: 'How is the Republican Party different from the Democratic Party?',
      answer: 'Republicans generally favor limited government, lower taxes, reduced regulations, free-market solutions, stronger border security, and traditional social values. Democrats typically support expanded government programs, higher taxes on the wealthy, more regulations, open immigration policies, and progressive social policies.'
    },
    {
      id: 3,
      question: 'When and why was the Republican Party founded?',
      answer: 'The Republican Party was founded on March 20, 1854, in Ripon, Wisconsin. It was created primarily by anti-slavery activists who opposed the Kansas-Nebraska Act, which allowed for the expansion of slavery. The party was formed to oppose the spread of slavery and promote free labor, free land, and free men.'
    },
    {
      id: 4,
      question: 'What is the significance of the elephant symbol?',
      answer: 'The elephant became the symbol of the Republican Party thanks to political cartoonist Thomas Nast. In an 1874 Harper\'s Weekly cartoon, Nast depicted the Republican vote as an elephant, portraying it as powerful, dignified, and intelligent. The symbol stuck and has represented the party ever since.'
    },
    {
      id: 5,
      question: 'What are some major accomplishments of Republican presidents?',
      answer: 'Republican presidents have achieved many significant accomplishments: Abraham Lincoln preserved the Union and ended slavery, Theodore Roosevelt established national parks and broke up monopolies, Dwight Eisenhower created the Interstate Highway System, Ronald Reagan revitalized the economy and helped end the Cold War, and Donald Trump achieved historic peace deals in the Middle East and record-low unemployment before the pandemic.'
    },
    {
      id: 6,
      question: 'What is the Republican stance on taxes and the economy?',
      answer: 'Republicans generally support lower taxes for individuals and businesses, believing this stimulates economic growth and job creation. They favor free market principles, deregulation to reduce business costs, balanced budgets, and fiscal responsibility. Republicans typically oppose excessive government spending and high national debt.'
    },
    {
      id: 7,
      question: 'What is the Republican view on immigration?',
      answer: 'Republicans typically support legal immigration while opposing illegal immigration. They advocate for secure borders, merit-based immigration systems, and enforcing existing immigration laws. Many Republicans believe in protecting American workers and maintaining national sovereignty through controlled, legal immigration processes.'
    },
    {
      id: 8,
      question: 'How do Republicans approach healthcare?',
      answer: 'Republicans generally favor market-based healthcare solutions with less government intervention. They support increasing competition among insurers, allowing purchases across state lines, expanding Health Savings Accounts, protecting those with pre-existing conditions, and giving states more flexibility in managing healthcare programs. They typically oppose single-payer or government-run healthcare systems.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">The Republican Party</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn about the Grand Old Party's history, values, achievements, and what it means to be a Republican in America today.
          </p>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex space-x-2 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'bg-patriot-red text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={tab.icon} className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Overview Section */}
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <h2 className="text-2xl font-bold text-patriot-navy mb-4">What is the Republican Party?</h2>
                  <div className="space-y-4 text-gray-700">
                    <p>
                      The Republican Party, commonly referred to as the GOP (Grand Old Party), is one of the two major contemporary political parties in the United States. Founded in 1854 by anti-slavery activists, the Republican Party has championed the ideals of constitutional government, fiscal conservatism, and individual liberty throughout its history.
                    </p>
                    <p>
                      Republicans believe in limited government intervention in both economic and social spheres, promoting free markets, lower taxes, and traditional values. The party emphasizes personal responsibility, the importance of strong families, and the protection of constitutional rights, especially those enumerated in the Bill of Rights.
                    </p>
                    <p>
                      Today's Republican Party continues to stand for conservative principles while adapting to the challenges of the modern era. From its humble beginnings to becoming a major political force, the GOP has played a crucial role in shaping American history and continues to represent millions of Americans who believe in its core values.
                    </p>
                    <div className="flex items-center mt-6">
                      <div className="w-16 h-16 bg-patriot-red rounded-full flex items-center justify-center mr-4">
                        <span className="text-white text-3xl">🐘</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-patriot-navy">Party Symbol: The Elephant</h3>
                        <p className="text-sm text-gray-600">
                          The elephant has been the symbol of the Republican Party since the 1870s, representing strength, dignity, and intelligence.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-patriot-navy mb-4">Republican Quick Facts</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-patriot-red mr-2 mt-0.5" />
                      <span className="text-gray-700">Founded on March 20, 1854, in Ripon, Wisconsin</span>
                    </li>
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-patriot-red mr-2 mt-0.5" />
                      <span className="text-gray-700">First Republican President: Abraham Lincoln (1861-1865)</span>
                    </li>
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-patriot-red mr-2 mt-0.5" />
                      <span className="text-gray-700">Party Colors: Red, White, and Blue (with Red as the primary identifier)</span>
                    </li>
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-patriot-red mr-2 mt-0.5" />
                      <span className="text-gray-700">19 Republican presidents have served the United States</span>
                    </li>
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-patriot-red mr-2 mt-0.5" />
                      <span className="text-gray-700">GOP stands for "Grand Old Party"</span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-medium text-patriot-navy mb-2">Current Leadership</h4>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">RNC Chairwoman:</span> Ronna McDaniel
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">Senate Republican Leader:</span> Mitch McConnell
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">House Republican Leader:</span> Mike Johnson
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 border-t border-gray-200 pt-8">
                <h2 className="text-2xl font-bold text-patriot-navy mb-4">What Republicans Stand For</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {coreValues.map((value, index) => (
                    <motion.div
                      key={value.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-patriot-red bg-opacity-10 rounded-full flex items-center justify-center mr-3">
                          <SafeIcon icon={value.icon} className="h-5 w-5 text-patriot-red" />
                        </div>
                        <h3 className="font-semibold text-patriot-navy">{value.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm">{value.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="mt-8 flex justify-center">
                <Link to="/learning" className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
                  <span>Explore More Learning Modules</span>
                  <SafeIcon icon={FiChevronRight} className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}

          {/* History Section */}
          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">History of the Republican Party</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-3">Foundation (1854)</h3>
                  <p className="text-gray-700 mb-4">
                    The Republican Party was founded on March 20, 1854, in Ripon, Wisconsin, primarily by anti-slavery activists who opposed the Kansas-Nebraska Act, which would have allowed slavery to expand into the western territories. The party's founding members came together from various political backgrounds, including former Whigs, Free-Soilers, and anti-slavery Democrats.
                  </p>
                  <p className="text-gray-700">
                    The first unofficial Republican meeting was held in a small white schoolhouse in Ripon, organized by Alvan E. Bovay. The name "Republican" was chosen to hearken back to Thomas Jefferson's Democratic-Republican Party and to emphasize the party's commitment to republicanism and the principles of liberty and equality.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-3">The Civil War Era (1860-1865)</h3>
                  <p className="text-gray-700 mb-4">
                    In 1860, just six years after its founding, the Republican Party succeeded in electing Abraham Lincoln as President. Lincoln's election precipitated the secession of several Southern states and ultimately the Civil War. During this crucial period, the Republican Party stood firmly against the expansion of slavery and for the preservation of the Union.
                  </p>
                  <p className="text-gray-700 mb-4">
                    President Lincoln issued the Emancipation Proclamation in 1863, declaring freedom for enslaved people in Confederate states. After the Union victory, Republicans in Congress passed the 13th Amendment abolishing slavery, the 14th Amendment guaranteeing equal protection under the law, and the 15th Amendment granting voting rights to African American men.
                  </p>
                  <div className="flex items-center justify-center my-6">
                    <div className="flex flex-col items-center">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-patriot-red">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/ab/Abraham_Lincoln_O-77_matte_collodion_print.jpg" alt="Abraham Lincoln" className="w-full h-full object-cover" />
                      </div>
                      <p className="mt-2 text-sm text-gray-600 text-center">Abraham Lincoln<br />First Republican President</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-3">The Progressive Era (1901-1933)</h3>
                  <p className="text-gray-700 mb-4">
                    The early 20th century saw the rise of progressive Republicans like Theodore Roosevelt, who championed conservation efforts, anti-trust legislation, and regulations to protect workers and consumers. Roosevelt's "Square Deal" focused on controlling corporations, consumer protection, and conserving natural resources.
                  </p>
                  <p className="text-gray-700">
                    During this era, Republican President William Howard Taft continued Roosevelt's antitrust actions, and later, Republican President Calvin Coolidge presided over the economic prosperity of the "Roaring Twenties," emphasizing limited government and pro-business policies.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-3">Post-War Era (1945-1980)</h3>
                  <p className="text-gray-700 mb-4">
                    After World War II, Republican President Dwight D. Eisenhower oversaw the creation of the Interstate Highway System and sent federal troops to enforce school desegregation in Little Rock, Arkansas. During this period, the party generally supported a strong anti-communist foreign policy and moderate domestic policies.
                  </p>
                  <p className="text-gray-700">
                    The 1964 Civil Rights Act, though signed by Democratic President Lyndon Johnson, received stronger support from congressional Republicans than Democrats. Republican Senator Everett Dirksen played a crucial role in passing civil rights legislation, famously saying, "The time has come for equality of opportunity in sharing in government, in education, and in employment."
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-3">The Reagan Revolution (1980-1989)</h3>
                  <p className="text-gray-700 mb-4">
                    Ronald Reagan's presidency marked a significant turning point for the Republican Party. His administration emphasized tax cuts, deregulation, and a strong national defense. Reagan's economic policies, known as "Reaganomics," focused on reducing tax rates to stimulate economic growth, controlling the money supply to reduce inflation, reducing government spending, and deregulating the economy.
                  </p>
                  <p className="text-gray-700 mb-4">
                    Reagan also took a strong stance against communism and is credited with helping to bring about the end of the Cold War. His famous challenge to Soviet leader Mikhail Gorbachev—"Mr. Gorbachev, tear down this wall!"—became a symbol of his foreign policy approach.
                  </p>
                  <div className="flex items-center justify-center my-6">
                    <div className="flex flex-col items-center">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-patriot-red">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/1/16/Official_Portrait_of_President_Reagan_1981.jpg" alt="Ronald Reagan" className="w-full h-full object-cover" />
                      </div>
                      <p className="mt-2 text-sm text-gray-600 text-center">Ronald Reagan<br />40th President (1981-1989)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-patriot-navy mb-3">Modern Era (1990-Present)</h3>
                  <p className="text-gray-700 mb-4">
                    In the 1990s, Republicans gained control of Congress for the first time in decades under the leadership of Newt Gingrich and his "Contract with America." George W. Bush's presidency (2001-2009) was defined by the War on Terror following the September 11 attacks and domestic initiatives like tax cuts and education reform.
                  </p>
                  <p className="text-gray-700 mb-4">
                    Donald Trump's presidency (2017-2021) represented a populist shift within the party, focusing on "America First" policies, immigration enforcement, tax cuts, deregulation, and the appointment of conservative judges. Under Trump, the party achieved significant tax reform, historically low unemployment rates, and peace agreements in the Middle East.
                  </p>
                  <p className="text-gray-700">
                    Today's Republican Party continues to champion traditional conservative values while adapting to address the challenges and opportunities of the 21st century. The party remains committed to limited government, economic freedom, strong national defense, and the protection of individual liberties.
                  </p>
                </div>
              </div>
              
              <div className="mt-8 flex justify-center">
                <Link to="/learning" className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
                  <span>Back to Learning Hub</span>
                  <SafeIcon icon={FiChevronRight} className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}

          {/* Values Section */}
          {activeTab === 'values' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">Republican Core Values</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Individual Liberty</h3>
                  <p className="text-gray-700 mb-6">
                    Republicans believe that the power of the individual—not the government—is at the core of American exceptionalism. The party champions personal freedom and responsibility, limited government intervention in people's lives, and the protection of constitutional rights. Republicans emphasize that liberty requires citizens to take personal responsibility for their actions and decisions.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Limited Government</h3>
                  <p className="text-gray-700 mb-6">
                    A foundational Republican principle is that government should be limited in scope and power. Republicans believe the federal government should only perform functions explicitly authorized by the Constitution, with all other powers reserved for the states or the people. This approach promotes federalism, where states serve as "laboratories of democracy" to find solutions that best fit their citizens' needs.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Free Market Economy</h3>
                  <p className="text-gray-700 mb-6">
                    Republicans advocate for free market economic principles, believing that economic freedom leads to opportunity and prosperity for all Americans. The party supports reducing regulations that burden businesses, lowering taxes to stimulate growth and job creation, and promoting international trade agreements that benefit American workers and companies. Republicans believe that a vibrant private sector—not government—is the true engine of economic growth and job creation.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Fiscal Responsibility</h3>
                  <p className="text-gray-700 mb-6">
                    The Republican Party champions responsible government spending, balanced budgets, and reduction of the national debt. Republicans believe that excessive government spending burdens future generations and constrains economic growth. The party advocates for tax policies that allow Americans to keep more of their hard-earned money while ensuring the government has sufficient resources to perform its constitutional functions.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Strong National Defense</h3>
                  <p className="text-gray-700 mb-6">
                    Republicans believe in maintaining a strong military capable of defending the United States and its interests around the world. The party supports robust defense spending to ensure American forces have the resources they need to deter aggression and respond to threats. Republicans also strongly support veterans and their families, recognizing the sacrifices they've made for the nation's security and freedom.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Traditional Values</h3>
                  <p className="text-gray-700 mb-6">
                    The Republican Party upholds traditional family values, religious liberty, and the sanctity of life. Republicans believe that strong families are the foundation of a strong society and that parents should have the primary role in raising their children. The party defends religious freedom as a fundamental constitutional right and supports policies that protect life from conception to natural death.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">Rule of Law</h3>
                  <p className="text-gray-700 mb-6">
                    Republicans believe in the strict interpretation and application of laws as written, particularly the Constitution. The party supports judges who interpret the law rather than create it from the bench, following the principle of originalism. Republicans advocate for a justice system that is fair, impartial, and respects due process while ensuring public safety through effective law enforcement.
                  </p>
                  
                  <h3 className="text-xl font-semibold text-patriot-navy mb-4">American Exceptionalism</h3>
                  <p className="text-gray-700 mb-6">
                    Republicans believe that America is an exceptional nation founded on the principles of liberty, equality, and self-governance. The party celebrates American history and traditions while acknowledging that we must continue working to live up to our founding ideals. Republicans support patriotic education that instills pride in American citizenship and an understanding of our constitutional republic.
                  </p>
                </div>
              </div>
              
              <div className="mt-8 bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-patriot-navy mb-4">The Republican Platform</h3>
                <p className="text-gray-700 mb-4">
                  The Republican Party platform is a document that outlines the party's official positions on various issues. It is updated every four years during the Republican National Convention. The platform covers a wide range of topics, including economic policy, national security, immigration, healthcare, education, and social issues.
                </p>
                <p className="text-gray-700">
                  While the specific details of the platform may evolve over time to address emerging challenges and opportunities, the core Republican values of individual liberty, limited government, free markets, fiscal responsibility, strong national defense, traditional values, and the rule of law remain constant guiding principles.
                </p>
              </div>
              
              <div className="mt-8 flex justify-center">
                <Link to="/learning" className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
                  <span>Continue Learning</span>
                  <SafeIcon icon={FiChevronRight} className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}

          {/* Fun Facts Section */}
          {activeTab === 'facts' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">Republican Fun Facts</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {funFacts.map((fact, index) => (
                  <motion.div
                    key={fact.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow duration-300"
                  >
                    <h3 className="font-semibold text-patriot-navy mb-2">{fact.title}</h3>
                    <p className="text-gray-600">{fact.description}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-8 bg-patriot-navy rounded-lg p-6 text-white">
                <h3 className="text-xl font-semibold mb-4">Did You Know?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white bg-opacity-10 rounded-lg p-4">
                    <p className="text-lg font-medium mb-2">The First Republican Convention</p>
                    <p className="text-gray-200">
                      The first Republican National Convention was held in Philadelphia in 1856, where John C. Frémont was nominated for president with the slogan "Free soil, free labor, free speech, free men, Frémont."
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-lg p-4">
                    <p className="text-lg font-medium mb-2">Republican Women Pioneers</p>
                    <p className="text-gray-200">
                      The Republican Party has a strong history of supporting women's rights. The first woman to run for president, Victoria Woodhull, ran on the Republican ticket in 1872, nearly 50 years before women could vote nationally.
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-lg p-4">
                    <p className="text-lg font-medium mb-2">Republican Civil Rights Legacy</p>
                    <p className="text-gray-200">
                      The Republican Party was founded partly on opposition to slavery, and Republicans were instrumental in passing the 13th, 14th, and 15th Amendments, which abolished slavery, guaranteed equal protection, and granted voting rights to African American men.
                    </p>
                  </div>
                  <div className="bg-white bg-opacity-10 rounded-lg p-4">
                    <p className="text-lg font-medium mb-2">The Youngest Republican President</p>
                    <p className="text-gray-200">
                      Theodore Roosevelt became the youngest president in U.S. history at age 42 when he assumed office after the assassination of President McKinley. He championed conservation efforts, establishing national parks and forests.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 flex justify-center">
                <Link to="/learning" className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
                  <span>More Learning Resources</span>
                  <SafeIcon icon={FiChevronRight} className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}

          {/* Achievements Section */}
          {activeTab === 'achievements' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">Republican Achievements Through History</h2>
              
              <div className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-patriot-red ml-6 hidden md:block"></div>
                <div className="space-y-12">
                  {achievements.map((achievement, index) => (
                    <motion.div
                      key={`${achievement.year}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex flex-col md:flex-row"
                    >
                      <div className="md:w-1/4 flex justify-start md:justify-end mb-4 md:mb-0">
                        <div className="flex items-center">
                          <div className="bg-patriot-red text-white px-3 py-1 rounded-lg font-medium md:mr-5 z-10">
                            {achievement.year}
                          </div>
                          <div className="w-3 h-3 bg-patriot-red rounded-full hidden md:block"></div>
                        </div>
                      </div>
                      <div className="md:w-3/4 bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow duration-300">
                        <h3 className="font-semibold text-patriot-navy mb-1">{achievement.title}</h3>
                        {achievement.president && (
                          <p className="text-sm text-gray-500 mb-2">President: {achievement.president}</p>
                        )}
                        <p className="text-gray-600">{achievement.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="mt-12 bg-patriot-red bg-opacity-5 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-patriot-navy mb-4">The Impact of Republican Policies</h3>
                <p className="text-gray-700 mb-4">
                  Throughout American history, Republican policies have had profound and lasting impacts on the nation. From ending slavery and securing civil rights to building national infrastructure and strengthening the economy, Republican achievements have helped shape America into the country it is today.
                </p>
                <p className="text-gray-700">
                  Republican administrations have consistently championed individual liberty, free markets, and strong national defense, while working to limit government overreach and protect constitutional rights. These principles have guided Republican leaders as they've addressed the challenges and opportunities of their times, always with the goal of building a stronger, more prosperous America.
                </p>
              </div>
              
              <div className="mt-8 flex justify-center">
                <Link to="/learning" className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
                  <span>Return to Learning Hub</span>
                  <SafeIcon icon={FiChevronRight} className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}

          {/* FAQ Section */}
          {activeTab === 'faq' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-2xl font-bold text-patriot-navy mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                {faqs.map((faq) => (
                  <div key={faq.id} className="border border-gray-200 rounded-lg">
                    <button
                      className="w-full text-left px-6 py-4 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-patriot-red focus:ring-offset-2"
                      onClick={() => toggleFaq(faq.id)}
                    >
                      <span className="font-medium text-patriot-navy">{faq.question}</span>
                      <SafeIcon
                        icon={expandedFaqs.includes(faq.id) ? FiChevronDown : FiChevronRight}
                        className={`h-5 w-5 text-patriot-red transition-transform duration-200 ${
                          expandedFaqs.includes(faq.id) ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaqs.includes(faq.id) && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-8 bg-patriot-navy rounded-lg p-6 text-white">
                <h3 className="text-xl font-semibold mb-4">Have More Questions?</h3>
                <p className="text-gray-200 mb-4">
                  If you have additional questions about the Republican Party, its history, values, or positions on specific issues, there are many resources available to help you learn more.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a
                    href="https://www.gop.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors duration-200 rounded-lg p-4 flex items-center"
                  >
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3">
                      <span className="text-patriot-red text-xl">🐘</span>
                    </div>
                    <div>
                      <p className="font-medium">Official GOP Website</p>
                      <p className="text-sm text-gray-300">Visit the Republican National Committee's official site</p>
                    </div>
                  </a>
                  <a
                    href="https://www.republicanleader.gov/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors duration-200 rounded-lg p-4 flex items-center"
                  >
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3">
                      <span className="text-patriot-red text-xl">🏛️</span>
                    </div>
                    <div>
                      <p className="font-medium">Congressional Republicans</p>
                      <p className="text-sm text-gray-300">Learn about Republican legislative priorities</p>
                    </div>
                  </a>
                </div>
              </div>
              
              <div className="mt-8 flex justify-center">
                <Link to="/learning" className="inline-flex items-center px-4 py-2 bg-patriot-red text-white rounded-lg hover:bg-patriot-red-dark transition-colors duration-200">
                  <span>Explore More Topics</span>
                  <SafeIcon icon={FiChevronRight} className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepublicanParty;